<template>
	<v-expansion-panels v-model="expandedPanels" @change="onExpandChange">
		<v-expansion-panel v-for="(chartData, chartId) in charts" :key="chartId" class="my-1 py-3 pl-2">
			<v-expansion-panel-header class="hello">
				{{ chartData.title }}
				<template v-slot:actions>
					<v-icon>mdi-chevron-down</v-icon>
				</template>
			</v-expansion-panel-header>

			<v-expansion-panel-content>
				<div v-if="chartData.isBP || chartData.isBGlucose">
					<div :id="'dashboard_' + chartId" class="dashboard"></div>
					<div :id="'control_' + chartId" class="control-container"></div>
					<div :id="'chart_' + chartId"></div>
				</div>

				<div v-else :id="'chart_' + chartId"></div>
			</v-expansion-panel-content>
		</v-expansion-panel>
	</v-expansion-panels>
</template>

<script>
import { getValueByUnit } from '@/utils/utilities';
import { mapGetters } from 'vuex';

/* global google */
export default {
	name: 'VitalsChart',
	props: {
		rawData: {
			type: Array,
			default: () => [],
		},
	},
	data () {
		return {
			rawDataFormatted: [],
			expandedPanels: [],
			renderedCharts: new Set(),
		};
	},
	computed: {
		...mapGetters({ userSettings: 'authentication/getUserSettings' }),
		charts () {
			const charts = {};
			const grouped = {};

			this.rawDataFormatted.forEach((v) => {
				if (!grouped[v.vitalType]) grouped[v.vitalType] = [];
				grouped[v.vitalType].push(v);
			});

			Object.keys(grouped).forEach((type) => {
				const data = grouped[type];
				const chartId = 'chart_' + type.replace(/[^a-zA-Z0-9]/g, '');
				const lower = type.toLowerCase();
				const isBP = lower.includes('blood pressure');
				const isBGlucose = lower.includes('blood glucose');

				if (isBP) {
					const rows = [['Date', 'Systolic', 'Diastolic', { type: 'string', role: 'tooltip' }]];
					data.forEach((v) => {
						const [sys, dia] = v.valueFormatted.split('/').map((x) => parseInt(x));
						const d = new Date(v.vitalDate);
						rows.push([d, sys, dia, `${d.toISOString().split('T')[0]} ${v.value}`]);
					});

					charts[chartId] = {
						title: type,
						type: 'LineChart',
						isBP: true,
						isBGlucose: false,
						data: rows,
						unit: data[0].unitFormatted,
					};
				} else {
					const rows = [['Date', type, { type: 'string', role: 'tooltip' }]];

					data.forEach((v) => {
						const d = new Date(v.vitalDate);
						rows.push([d, parseFloat(v.valueFormatted), `${d.toISOString().split('T')[0]} ${v.value}`]);
					});

					charts[chartId] = {
						title: type,
						type: 'LineChart',
						isBP: false,
						isBGlucose: isBGlucose,
						data: rows,
						unit: data[0].unitFormatted,
					};
				}
			});

			return charts;
		},
	},
	mounted () {
		this.rawDataFormatted = this.rawData.map((r) => {
			const outputValue = getValueByUnit(
				r.value,
				r.vitalType === 'Blood Glucose' ? '' : this.userSettings.Units,
				r.vitalType === 'Blood Glucose' ? this.userSettings.Concentration : '',
			);
			return {
				vitalType: r.vitalType,
				value: r.value,
				valueFormatted: outputValue.value,
				unitFormatted: outputValue.unit,
				vitalDate: r.vitalDate,
			};
		});

		// Load Google Charts lib only (not draw)
		if (!window.google) {
			const script = document.createElement('script');
			script.src = 'https://www.gstatic.com/charts/loader.js';
			script.onload = () => google.charts.load('current', { packages: ['corechart', 'controls'] });
			document.head.appendChild(script);
		} else {
			google.charts.load('current', { packages: ['corechart', 'controls'] });
		}
	},

	methods: {
		getValueByUnit,
		getHAxisViewWindow (dataRows) {
			if (!dataRows || dataRows.length < 2) {
				const firstDate = dataRows.length > 1 ? dataRows[1][0] : new Date();
				const minDate = new Date(firstDate.getTime() - 3600000);
				const maxDate = new Date(firstDate.getTime() + 3600000);
				return { min: minDate, max: maxDate };
			}

			const dates = dataRows.slice(1).map((row) => row[0]);
			const actualMinDate = new Date(Math.min(...dates));
			const actualMaxDate = new Date(Math.max(...dates));
			const currentSpanMs = actualMaxDate.getTime() - actualMinDate.getTime();

			if (currentSpanMs < 7200000) {
				const centerDate = new Date(actualMinDate.getTime() + currentSpanMs / 2);
				return {
					min: new Date(centerDate.getTime() - 3600000),
					max: new Date(centerDate.getTime() + 3600000),
				};
			}
			// Otherwise, use the natural span
			return { min: actualMinDate, max: actualMaxDate };
		},
		drawAllCharts () {
			google.charts.load('current', { packages: ['corechart', 'controls'] });
			google.charts.setOnLoadCallback(() => {
				Object.entries(this.charts).forEach(([chartId, cfg]) => {
					const dataTable = google.visualization.arrayToDataTable(cfg.data);

					if (cfg.isBP || cfg.isBGlucose) {
						const dashboard = new google.visualization.Dashboard(document.getElementById('dashboard_' + chartId));

						const control = new google.visualization.ControlWrapper({
							controlType: 'ChartRangeFilter',
							containerId: 'control_' + chartId,
							options: {
								filterColumnIndex: 0,
								ui: {
									chartType: 'LineChart',
									chartOptions: {
										curveType: 'function',
										height: 60,
										chartArea: { width: '80%' },
									},
								},
							},
						});
						const chart = new google.visualization.ChartWrapper({
							chartType: 'LineChart',
							containerId: 'chart_' + chartId,
							options: {
								title: cfg.title,
								curveType: 'function',
								pointSize: 5,
								height: 400,
								legend: { position: 'bottom' },
								tooltip: { isHtml: true },
								hAxis: { title: 'Date' },
								vAxis: { title: cfg.unit },
								chartArea: { width: '80%', height: '70%' },
							},
						});

						dashboard.bind(control, chart);
						dashboard.draw(dataTable);
					} else {
						const viewWindowOptions = this.getHAxisViewWindow(cfg.data);
						const chart = new google.visualization.LineChart(document.getElementById('chart_' + chartId));
						chart.draw(dataTable, {
							title: cfg.title,
							curveType: 'function',
							pointSize: 5,
							height: 400,
							legend: { position: 'bottom' },
							tooltip: { isHtml: true },
							hAxis: {
								title: 'Date',
								viewWindow: {
									min: viewWindowOptions.min,
									max: viewWindowOptions.max,
								},
							},
							vAxis: { title: cfg.unit },
							chartArea: { width: '80%', height: '70%' },
						});
					}
				});
			});
		},
		onExpandChange (index) {
			this.$nextTick(() => {
				const chartKey = Object.keys(this.charts)[index];
				const cfg = this.charts[chartKey];
				if (!cfg) return;

				setTimeout(() => {
					const isReady =
						cfg.isBP || cfg.isBGlucose
							? document.getElementById('dashboard_' + chartKey) &&
							  document.getElementById('chart_' + chartKey) &&
							  document.getElementById('control_' + chartKey)
							: document.getElementById('chart_' + chartKey);

					if (isReady && !this.renderedCharts.has(chartKey)) {
						this.drawSingleChart(chartKey);
						this.renderedCharts.add(chartKey);
					}
				}, 150);
			});
		},

		drawSingleChart (chartId) {
			const cfg = this.charts[chartId];
			if (!cfg || !cfg.data?.length) return;

			const dataTable = google.visualization.arrayToDataTable(cfg.data);

			if (cfg.isBP || cfg.isBGlucose) {
				const dashboard = new google.visualization.Dashboard(document.getElementById('dashboard_' + chartId));

				const control = new google.visualization.ControlWrapper({
					controlType: 'ChartRangeFilter',
					containerId: 'control_' + chartId,
					options: {
						filterColumnIndex: 0,
						ui: {
							chartType: 'LineChart',
							chartOptions: {
								curveType: 'function',
								height: 60,
								chartArea: { width: '80%' },
							},
						},
					},
				});

				const chart = new google.visualization.ChartWrapper({
					chartType: 'LineChart',
					containerId: 'chart_' + chartId,
					options: {
						title: cfg.title,
						curveType: 'function',
						pointSize: 5,
						height: 400,
						legend: { position: 'bottom' },
						tooltip: { isHtml: true },
						hAxis: { title: 'Date' },
						vAxis: { title: cfg.unit },
						chartArea: { width: '80%', height: '70%' },
					},
				});

				dashboard.bind(control, chart);
				dashboard.draw(dataTable);
			} else {
				const viewWindowOptions = this.getHAxisViewWindow(cfg.data);
				const chart = new google.visualization.LineChart(document.getElementById('chart_' + chartId));
				chart.draw(dataTable, {
					title: cfg.title,
					curveType: 'function',
					pointSize: 5,
					height: 400,
					legend: { position: 'bottom' },
					tooltip: { isHtml: true },
					hAxis: {
						title: 'Date',
						viewWindow: {
							min: viewWindowOptions.min,
							max: viewWindowOptions.max,
						},
					},
					vAxis: { title: cfg.unit },
					chartArea: { width: '80%', height: '70%' },
				});
			}
		},
	},
};
</script>

<style scoped>
.chart-container {
	width: 100%;
}

.control-container {
	width: 100%;
}

.dashboard {
	margin-bottom: 8px;
}

::v-deep .mdi-chevron-down::before {
	color: black !important;
}
</style>
